import { useEffect, useState } from 'react';
import { DetailSession } from '@streem/sdk-core';
import { Column, Spinner, styled, TranscriptContainer } from '@streem/ui-react';
import { recordElementClicked, recordElementScrolled } from '@streem/analytics';
import { useTranscriptions } from '../../hooks/use_transcriptions';
import { MediaGalleryHeader } from './shared.styles';
import { TranscriptionError } from './transcription_error';

interface TranscriptionsProps {
    detailSession: DetailSession;
}
const analyticsHandlers = {
    recordClickEvent: (id: string) => recordElementClicked(id),
    recordScrollEvent: (id: string, roomSid: string) => recordElementScrolled(id, roomSid),
};
export function Transcriptions({ detailSession }: TranscriptionsProps): JSX.Element {
    return (
        <TranscriptionWrapper>
            <MediaGalleryHeader>Audio Transcript</MediaGalleryHeader>
            <TranscriptionContent detailSession={detailSession} />
        </TranscriptionWrapper>
    );
}
function TranscriptionContent({ detailSession }: TranscriptionsProps): JSX.Element {
    const [selectedItemIndex, setSelectedItemIndex] = useState<number | undefined>();

    const {
        transcriptBlocks,
        transcriptionsInitialized,
        transcriptionError,
        refresh,
        registerIndexChangedCallback,
        unregisterIndexChangedCallback,
        autoScrollModeActive,
        handleTimeStampJump,
        handleSearch,
        searchValue,
        clearSearch,
        updateAutoScrollMode,
    } = useTranscriptions(detailSession);

    useEffect(() => {
        registerIndexChangedCallback(setSelectedItemIndex);
        return () => {
            unregisterIndexChangedCallback(setSelectedItemIndex);
        };
    }, [registerIndexChangedCallback, unregisterIndexChangedCallback]);

    let transcriptionsContent: JSX.Element;
    if (transcriptionError != null) {
        transcriptionsContent = <TranscriptionError onRefresh={refresh} />;
    } else if (transcriptionsInitialized) {
        transcriptionsContent = (
            <TranscriptContainer
                transcriptBlocks={transcriptBlocks}
                selectedItemIndex={selectedItemIndex}
                autoScrollModeActive={autoScrollModeActive}
                handleTimeStampJump={handleTimeStampJump}
                searchPlaceholder="Search Transcript"
                onSearch={handleSearch}
                searchValue={searchValue}
                clearSearch={clearSearch}
                analyticsHandlers={analyticsHandlers}
                updateAutoScrollMode={updateAutoScrollMode}
                roomSid={detailSession.roomId.toString()}
            />
        );
    } else {
        transcriptionsContent = (
            <SpinnerWrapper data-testid="transcription-spinner-wrapper">
                <Spinner size="3rem" />
            </SpinnerWrapper>
        );
    }
    return transcriptionsContent;
}

const TranscriptionWrapper = styled(Column)({
    gridColumn: 'span 1',
    gridRow: 'span 2',
});

const SpinnerWrapper = styled(TranscriptionWrapper)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '15rem',
    border: `0.063rem solid ${theme.colors.grey10}`,
    borderRadius: '0.5rem',
}));
